import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { Grid } from "@mui/material"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { useCallback, useMemo, useState } from "react"
import { MRT_ColumnDef, MRT_Row, MRT_TableInstance } from "material-react-table"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { fetchPriceLists, PriceListQuery } from "../../priceList/priceListApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    ExtraServicesQuery,
    fetchExtraServices,
    FetchExtraServicesResponse,
    sortExtraServiceInPriceList,
} from "../extraServicesApi"
import { getExtraServiceRowId, reorderExtraServices } from "../extraServicesHelpers"
import { useOptimisticOrder } from "../../products/hooks/useOptimisticOrder"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"
import { ExternalServiceEntry, ExtraServicePricingType } from "../extraServicesTypes"
import {
    defaultConfirmModalState,
    DefaultConfirmModalStateType,
    DeleteExtraServiceDialog,
} from "../components/DeleteExtraServiceDialog"
import { renderRowActionMenuItems, useRowActionMenuItemsStyles } from "../components/renderRowActionMenuItems"
import { useMoney } from "../../../../../modules/currency/useMoney"
import { toDineroOrUndefined } from "../../../../../modules/currency/currencyLib"

const useStyles = makeStyles()(() => ({
    headCell: {
        "& .Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

const transformProductsData = (data: FetchExtraServicesResponse) => data.extraServices

export function ExtraServices() {
    const { classes } = useStyles()
    const { classes: rowActionClasses } = useRowActionMenuItemsStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()
    const moneyFactory = useMoney({})

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)

    const services = useApiQuery(fetchExtraServices, {
        queryName: ExtraServicesQuery.LIST,
        dependencies: {
            merchantId: merchantId,
        },
        select: transformProductsData,
    })

    // The sort endpoint is price-list-scoped. Extra services aren't placed in a specific
    // price list yet, so default to the merchant's first active price list (same default
    // the product pages use). TODO: replace with an explicit price-list selector if needed.
    const { data: priceLists = [] } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: { merchantId },
        select: (data) => data?.priceLists ?? [],
    })

    const priceListId = useMemo(() => {
        const firstActive = priceLists.find((priceList) => priceList.isActive)

        return firstActive?.id ?? priceLists[0]?.id
    }, [priceLists])

    // Optimistic local ordering so a drag shows instantly; rolls back on failure.
    const { orderedRows, applyOptimisticMove, rollback } = useOptimisticOrder(services.data, reorderExtraServices)

    const { ApiQueryTable, columnFactory } = useApiQueryTable({
        data: orderedRows,
        isInitialLoading: services.isInitialLoading,
        isFetching: services.isFetching,
        isError: services.isError,
    })

    // Sort is applied optimistically on drop. On failure: roll back to the pre-move
    // order, then re-throw so the API error is surfaced as a snackbar.
    const { mutate: callSortExtraService } = useApiMutation(sortExtraServiceInPriceList, {
        async onError(_, error) {
            rollback()
            throw error
        },
    })

    const allowSort = !useMatchEnvironment([EnvironmentType.QA, EnvironmentType.Prod])

    const handleDragEnd = useCallback(
        (table: MRT_TableInstance<ExternalServiceEntry>) => {
            const { draggingRow, hoveredRow } = table.getState()
            if (!draggingRow || !hoveredRow) return

            const extraServiceId = draggingRow.original.id
            if (extraServiceId === undefined) return

            const targetRow = hoveredRow as MRT_Row<ExternalServiceEntry>

            // Show the new order immediately (snapshots internally for rollback).
            applyOptimisticMove(draggingRow.original, targetRow.original)

            if (priceListId === undefined) return

            callSortExtraService({
                priceListId,
                extraServiceId,
                body: {
                    moveTo: { position: targetRow.index + 1 },
                },
            })
        },
        [priceListId, applyOptimisticMove, callSortExtraService],
    )

    const dragHandleProps = useCallback(
        ({ table }: { table: MRT_TableInstance<ExternalServiceEntry> }) => ({
            onDragEnd: () => handleDragEnd(table),
        }),
        [handleDragEnd],
    )

    const columns = useMemo<MRT_ColumnDef<ExternalServiceEntry>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                size: COLUMN_SIZE.XL,
                header: l10n.getString("extra-services-services-column"),
                Cell: ({ row }) => {
                    const entry = row.original

                    if (entry.pricingType === ExtraServicePricingType.FLAT_FEE) {
                        const amount = toDineroOrUndefined(entry.price)
                        const subtitle1 = amount
                            ? `${l10n.getString("extra-services-price-subline-fixed")}: ${moneyFactory.format(amount)}`
                            : ""

                        return <StandardTwoLinesCell title={entry.name} subtitle1={subtitle1} />
                    }

                    const perSeat = toDineroOrUndefined(entry.pricePerSeat)
                    const min = toDineroOrUndefined(entry.minimumPrice)
                    const max = toDineroOrUndefined(entry.maximumPrice)

                    const subtitle1 = perSeat
                        ? `${l10n.getString("extra-services-price-subline-per-seat")}: ${moneyFactory.format(perSeat)}`
                        : ""
                    const subtitle2 = min
                        ? `${l10n.getString("extra-services-price-subline-min")}: ${moneyFactory.format(min)}`
                        : null
                    const subtitle3 = max
                        ? `${l10n.getString("extra-services-price-subline-max")}: ${moneyFactory.format(max)}`
                        : null

                    return (
                        <StandardTwoLinesCell
                            title={entry.name}
                            subtitle1={subtitle1}
                            subtitle2={subtitle2}
                            subtitle3={subtitle3}
                        />
                    )
                },
            }),
            columnFactory.typeColumn({
                id: "serviceType",
                header: l10n.getString("extra-services-service-type-column"),
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                accessorFn: (row) =>
                    row.serviceType
                        ? l10n.getStringForEnum(
                              "extra-services-create-form-type-dropdown-item",
                              row.serviceType,
                              row.serviceType,
                          )
                        : "",
            }),
            columnFactory.typeColumn({
                id: "pricingType",
                header: l10n.getString("extra-services-price-type-column"),
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                accessorFn: (row) =>
                    row.pricingType
                        ? l10n.getStringForEnum(
                              "extra-services-create-form-pricing-type-dropdown-item",
                              row.pricingType,
                              row.pricingType,
                          )
                        : "",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("extra-services-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory, moneyFactory],
    )

    return (
        <>
            <LocalizedStrict id="extra-services-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Extra Services"
                    page={Page.MERCHANT_EXTRA_SERVICES}
                    rightElement={
                        <LocalizedStrict id="extra-services-create-service-button">
                            <StandardButton
                                onClick={() => {
                                    navigate(MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE)
                                }}
                            >
                                Create extra service
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-services">
                            <Breadcrumb>Extra services</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    memoMode="cells"
                    getRowId={getExtraServiceRowId}
                    enableRowOrdering={allowSort}
                    muiTableBodyRowDragHandleProps={dragHandleProps}
                    initialState={{ density: "spacious" }}
                    muiTableHeadCellProps={{ className: classes.headCell }}
                    renderRowActionMenuItems={({ row, closeMenu }) =>
                        renderRowActionMenuItems({
                            row,
                            closeMenu,
                            setConfirmModalState,
                            navigate,
                            deleteItemClassName: rowActionClasses.deleteItem,
                        })
                    }
                />

                {/* Confirm delete */}
                {confirmModalState.showConfirmModal && (
                    <DeleteExtraServiceDialog
                        confirmModalState={confirmModalState}
                        setConfirmModalState={setConfirmModalState}
                    />
                )}
            </Grid>
        </>
    )
}
